/**
 * Robust JSON Validator with descriptive error messages.
 */
import { ASAEScript } from './Schema';

export interface ValidationError {
  path: string;
  message: string;
  severity: 'error' | 'warning';
}

export class Validation {
  public static validate(script: any): ValidationError[] {
    const errors: ValidationError[] = [];

    if (!script || typeof script !== 'object') {
      errors.push({ path: 'root', message: 'Script must be a valid JSON object', severity: 'error' });
      return errors;
    }

    if (!script.version) {
      errors.push({ path: 'version', message: 'Missing "version" in script header', severity: 'warning' });
    }

    if (!script.scenes || !Array.isArray(script.scenes) || script.scenes.length === 0) {
      errors.push({ path: 'scenes', message: 'Script must contain a non-empty "scenes" array', severity: 'error' });
      return errors;
    }

    script.scenes.forEach((scene: any, sIdx: number) => {
      const scenePath = `scenes[${sIdx}]`;

      if (!scene.id) {
        errors.push({ path: `${scenePath}.id`, message: 'Scene must have a unique "id"', severity: 'error' });
      }

      if (typeof scene.duration !== 'number' || scene.duration <= 0) {
        errors.push({
          path: `${scenePath}.duration`,
          message: 'Scene duration must be a positive number in seconds',
          severity: 'error'
        });
      }

      if (scene.objects && Array.isArray(scene.objects)) {
        scene.objects.forEach((obj: any, oIdx: number) => {
          const objPath = `${scenePath}.objects[${oIdx}]`;
          if (!obj.id) {
            errors.push({ path: `${objPath}.id`, message: 'Object must have an "id"', severity: 'error' });
          }
          if (!obj.type) {
            errors.push({ path: `${objPath}.type`, message: 'Object must have a "type"', severity: 'error' });
          }

          if (obj.animations && Array.isArray(obj.animations)) {
            obj.animations.forEach((anim: any, aIdx: number) => {
              const animPath = `${objPath}.animations[${aIdx}]`;
              if (!anim.property) {
                errors.push({ path: `${animPath}.property`, message: 'Animation track must specify "property"', severity: 'error' });
              }
              if (!anim.keyframes || !Array.isArray(anim.keyframes) || anim.keyframes.length === 0) {
                errors.push({ path: `${animPath}.keyframes`, message: 'Animation keyframes must be a non-empty array', severity: 'error' });
              }
            });
          }
        });
      }
    });

    return errors;
  }
}
